import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Grid,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import React, { useState, useMemo } from "react";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";

const CreditOverview = () => {
  const {
    data: accounts,
    isLoading,
    isError,
    error,
  } = useApiEp(`/credit/credit-overview`);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  // Calculate statistics
  const stats = useMemo(() => {
    if (!accounts) return {};
    return {
      neverRecharged: accounts.filter((a) => !a.wallet).length,
      zeroBalance: accounts.filter((a) => a.wallet === 0).length,
      lessThan5: accounts.filter((a) => a.wallet && a.wallet < 5).length,
      lessThan100: accounts.filter((a) => a.wallet && a.wallet < 100).length,
      greaterThan1000: accounts.filter((a) => a.wallet && a.wallet > 1000)
        .length,
      greaterThan10000: accounts.filter((a) => a.wallet && a.wallet > 10000)
        .length,
    };
  }, [accounts]);

  // Filter and sort accounts
  const filteredAccounts = useMemo(() => {
    if (!accounts) return [];

    let result = [...accounts];

    // Apply search filter
    if (searchTerm) {
      result = result.filter((account) =>
        account.customerId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply balance filter
    switch (balanceFilter) {
      case "never":
        result = result.filter((account) => !account.wallet);
        break;
      case "zero":
        result = result.filter((account) => account.wallet === 0);
        break;
      case "low":
        result = result.filter(
          (account) => account.wallet && account.wallet < 100
        );
        break;
      case "high":
        result = result.filter(
          (account) => account.wallet && account.wallet > 1000
        );
        break;
      case "critical":
        result = result.filter(
          (account) => account.wallet && account.wallet < 5
        );
        break;
      default:
        // "all" - no filter
        break;
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        // Handle cases where wallet might not exist
        const valA = a[sortConfig.key] || 0;
        const valB = b[sortConfig.key] || 0;

        if (valA < valB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [accounts, searchTerm, balanceFilter, sortConfig]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setBalanceFilter("all");
    setSortConfig({ key: null, direction: "asc" });
  };

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <BackBtn />
      <PageTitle>Credit Overview</PageTitle>

      {/* Search and Filter Bar */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <IconButton
                size="small"
                onClick={() => setSearchTerm("")}
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }}
          sx={{ minWidth: 250, flexGrow: 1 }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={balanceFilter}
            onChange={(e) => setBalanceFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="all">All Balances</MenuItem>
            <MenuItem value="never">Never Recharged</MenuItem>
            <MenuItem value="zero">Zero Balance</MenuItem>
            <MenuItem value="critical">Critical (&lt; ₹5)</MenuItem>
            <MenuItem value="low">Low (&lt; ₹100)</MenuItem>
            <MenuItem value="high">High (&gt; ₹1000)</MenuItem>
          </Select>
        </FormControl>

        {(searchTerm || balanceFilter !== "all") && (
          <Tooltip title="Clear all filters">
            <IconButton onClick={clearFilters}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Never Recharged"
            value={stats.neverRecharged}
            onClick={() => setBalanceFilter("never")}
            active={balanceFilter === "never"}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Zero Balance"
            value={stats.zeroBalance}
            onClick={() => setBalanceFilter("zero")}
            active={balanceFilter === "zero"}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Critical (&lt; ₹5)"
            value={stats.lessThan5}
            onClick={() => setBalanceFilter("critical")}
            active={balanceFilter === "critical"}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Low (&lt; ₹100)"
            value={stats.lessThan100}
            onClick={() => setBalanceFilter("low")}
            active={balanceFilter === "low"}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="High (&gt; ₹1000)"
            value={stats.greaterThan1000}
            onClick={() => setBalanceFilter("high")}
            active={balanceFilter === "high"}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            title="Premium (&gt; ₹10000)"
            value={stats.greaterThan10000}
            onClick={() => setBalanceFilter("premium")}
            active={balanceFilter === "premium"}
          />
        </Grid>
      </Grid>

      {/* Accounts Table */}
      <Typography variant="h6" sx={{ mt: 1, mb: 2 }}>
        Accounts ({filteredAccounts.length})
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                onClick={() => handleSort("customerId")}
                sx={{ cursor: "pointer", fontWeight: "bold" }}
              >
                Customer ID
                {sortConfig.key === "customerId" && (
                  <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                )}
              </TableCell>
              <TableCell
                align="right"
                onClick={() => handleSort("wallet")}
                sx={{ cursor: "pointer", fontWeight: "bold" }}
              >
                Wallet Balance
                {sortConfig.key === "wallet" && (
                  <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
                )}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAccounts.length > 0 ? (
              filteredAccounts.map((account) => (
                <TableRow key={account._id} hover>
                  <TableCell>{account.customerId}</TableCell>
                  <TableCell align="right">
                    {account.wallet ? (
                      <Chip
                        label={`₹${account.wallet.toFixed(2)}`}
                        color={
                          account.wallet > 1000
                            ? "success"
                            : account.wallet < 5
                            ? "error"
                            : "primary"
                        }
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Never recharged"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Link
                      href={`/home/${account._id}/recharge-area`}
                      underline="hover"
                      color="primary"
                    >
                      Recharge
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  No accounts match your filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

// Reusable StatCard component
const StatCard = ({ title, value, onClick, active }) => (
  <Paper
    elevation={active ? 6 : 3}
    sx={{
      p: 1.5,
      cursor: "pointer",
      border: active ? "2px solid #1976d2" : "none",
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: 6,
      },
    }}
    onClick={onClick}
  >
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: active ? "bold" : "normal" }}
    >
      {title}
    </Typography>
    <Typography variant="h5" color={active ? "primary" : "text.secondary"}>
      {value}
    </Typography>
  </Paper>
);

export default CreditOverview;
